import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { formatNoticeDate } from '@/features/centerNotice/formatDate';
import Table from '../../components/Table/Table';
import { RegularText, MediumText, DemiLightText } from '../../lib/Text';
import { colors } from '../../lib/colors';
import Badge from './components/Badge';
import ActionMenu from './components/ActionMenu';
import SvgIcon from '../../components/SvgIcon';
import { Col, Row, getItemWithExpiration } from '@/lib/utils';
import { useCenterNoticeList, useDeleteCenterNotice } from '@/features/centerNotice';
import { CenterNotice, TargetMemberStatus } from '@/features/centerNotice/types';
import Modal from '@/components/modal/Modal';
import ModalCreateNotice from './ModalCreateNotice';
import ModalNoticeDetail from './components/ModalNoticeDetail';
import { overlay } from 'overlay-kit';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';

const TARGET_STATUS_LABEL: Record<TargetMemberStatus, string> = {
    all: '전체',
    active: '활성',
    expired: '만료',
};

const NOTICE_PAGE_SIZE = 10;

function NoticePage() {
    const authObject = getItemWithExpiration('authObject');
    const centerId = authObject?.centerId ?? '';
    const [pagination, setPagination] = useState({ centerId, page: 1 });
    // 센터 변경을 반영하는 effect 전에도 새 센터는 반드시 첫 페이지를 조회한다.
    const page = pagination.centerId === centerId ? pagination.page : 1;
    const setPage = useCallback((nextPage: number) => {
        setPagination({ centerId, page: nextPage });
    }, [centerId]);

    const { data: noticeListData, isLoading, isFetching, isError, refetch } = useCenterNoticeList({
        centerId,
        page,
        limit: NOTICE_PAGE_SIZE,
        enabled: !!centerId,
    });

    const notices = noticeListData?.results ?? [];
    const totalResults = noticeListData?.totalResults ?? 0;
    const totalPages = Math.max(1, noticeListData?.totalPages ?? 1);
    const firstVisiblePage = Math.max(1, Math.min(page - 4, totalPages - 9));

    const [newNoticeIds, setNewNoticeIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        setPagination({ centerId, page: 1 });
        setNewNoticeIds(new Set());
    }, [centerId]);

    useEffect(() => {
        // 삭제 후 재조회가 성공한 경우에만 마지막 유효 페이지로 돌아간다.
        if (!isFetching && !isError && noticeListData?.page === page && page > totalPages) {
            setPage(totalPages);
        }
    }, [isFetching, isError, noticeListData, page, totalPages, setPage]);

    const handleCreated = useCallback((noticeId: string) => {
        setNewNoticeIds((prev) => new Set(prev).add(noticeId));
        setPage(1);
    }, [setPage]);

    const deleteMutation = useDeleteCenterNotice();

    const handleDelete = (noticeId: string) => {
        if (!centerId) return;
        overlay.open(({ unmount }) => (
            <Modal
                onClose={unmount}
                whiteViewWidth={450}
                borderRadius={20}
                isCloseButton={false}
                isWhiteOverFlowScroll={false}
            >
                <div className="px-[32px] py-[36px]">
                    <div className="mb-[38px] pl-[4px]">
                        <MediumText fontSize={22} lineHeight={30} color={colors.Text_1} letterSpacing={-0.5}>
                            해당 공지 사항을 삭제할까요?
                        </MediumText>
                    </div>
                    <Row className="gap-[16px] items-end flex justify-end">
                        <button
                            className="h-[40px] w-[160px] rounded-[12px] bg-[#F2F4F6] hover:bg-[#E8EAED] transition-colors"
                            onClick={unmount}
                        >
                            <RegularText fontSize={16} lineHeight={16} color={colors.Text_1}>
                                취소하기
                            </RegularText>
                        </button>
                        <button
                            className="h-[40px] w-[160px] rounded-[12px] bg-primary hover:bg-blue-600 transition-colors"
                            onClick={() => {
                                deleteMutation.mutate({ centerId, noticeId });
                                unmount();
                            }}
                        >
                            <RegularText fontSize={16} lineHeight={16} color="white">
                                삭제하기
                            </RegularText>
                        </button>
                    </Row>
                </div>
            </Modal>
        ));
    };

    const handleOpenCreateModal = () => {
        overlay.open(({ unmount }) => (
            <ModalCreateNotice onClose={unmount} centerId={centerId} onCreated={handleCreated} />
        ));
    };

    const handleOpenEditModal = (notice: CenterNotice) => {
        overlay.open(({ unmount }) => (
            <ModalCreateNotice onClose={unmount} centerId={centerId} mode="edit" initialNotice={notice} />
        ));
    };

    const handleOpenDuplicateModal = (notice: CenterNotice) => {
        overlay.open(({ unmount }) => (
            <ModalCreateNotice
                onClose={unmount}
                centerId={centerId}
                mode="duplicate"
                initialNotice={notice}
                onCreated={handleCreated}
            />
        ));
    };

    const handleOpenDetailModal = (notice: CenterNotice) => {
        overlay.open(({ unmount }) => (
            <ModalNoticeDetail
                notice={notice}
                onClose={unmount}
                onEdit={(n) => {
                    unmount();
                    handleOpenEditModal(n);
                }}
                onDuplicate={(n) => {
                    unmount();
                    handleOpenDuplicateModal(n);
                }}
                onDelete={(n) => {
                    unmount();
                    handleDelete(n.id);
                }}
            />
        ));
    };

    const columns = useMemo<ColumnDef<CenterNotice>[]>(
        () => [
            {
                accessorKey: 'targetMemberStatus',
                header: () => (
                    <DemiLightText fontSize={16} lineHeight={24} color={'#A8AEB5'} letterSpacing={-0.4}>
                        구분
                    </DemiLightText>
                ),
                size: 40,
                cell: (info) => {
                    const value = info.getValue() as TargetMemberStatus;
                    return (
                        <div className="flex w-full justify-center">
                            <DemiLightText fontSize={16} lineHeight={24} color="#A8AEB5" letterSpacing={-0.4}>
                                {TARGET_STATUS_LABEL[value]}
                            </DemiLightText>
                        </div>
                    );
                },
                enableSorting: false,
            },
            {
                accessorKey: 'title',
                header: () => (
                    <div className="flex w-full px-10 items-center gap-[5px]">
                        <DemiLightText fontSize={16} lineHeight={24} color={'#A8AEB5'} letterSpacing={-0.4}>
                            제목
                        </DemiLightText>
                    </div>
                ),
                size: 642,
                cell: (info) => {
                    const row = info.row.original;
                    const isNew = newNoticeIds.has(row.id);
                    return (
                        <div className="flex w-full px-10 items-center gap-[5px]">
                            <RegularText fontSize={16} lineHeight={24} color={'#191F28'} letterSpacing={-0.4}>
                                {row.title}
                            </RegularText>
                            {isNew && row.status !== 'scheduled' && (
                                <Badge variant="new">
                                    <RegularText fontSize={13} lineHeight={18} color="#3182F6">
                                        New
                                    </RegularText>
                                </Badge>
                            )}
                            {row.status === 'scheduled' && (
                                <Badge variant="default">
                                    <RegularText fontSize={14} lineHeight={21} color={colors.white}>
                                        예약 등록
                                    </RegularText>
                                </Badge>
                            )}
                        </div>
                    );
                },
                meta: {
                    align: 'start',
                },
                enableSorting: false,
            },
            {
                id: 'displayDate',
                header: () => (
                    <DemiLightText fontSize={16} lineHeight={24} color={'#A8AEB5'} letterSpacing={-0.4}>
                        등록 일시
                    </DemiLightText>
                ),
                size: 182,
                cell: (info) => (
                    <div className="flex justify-center">
                        <RegularText fontSize={16} lineHeight={24} color="#989FA7" letterSpacing={-0.4}>
                            {formatNoticeDate(info.row.original)}
                        </RegularText>
                    </div>
                ),
                enableSorting: false,
            },
            {
                id: 'actions',
                header: () => (
                    <DemiLightText fontSize={16} lineHeight={24} color={'#A8AEB5'} letterSpacing={-0.4}>
                        편집
                    </DemiLightText>
                ),
                size: 122,
                cell: (info) => (
                    <div className="flex justify-center">
                        <ActionMenu
                            onDuplicate={
                                info.row.original.status !== 'scheduled'
                                    ? () =>
                                          handleOpenDuplicateModal({
                                              ...info.row.original,
                                              title: `[복제] ${info.row.original.title}`,
                                          })
                                    : undefined
                            }
                            onEdit={
                                info.row.original.status !== 'scheduled'
                                    ? () => handleOpenEditModal(info.row.original)
                                    : undefined
                            }
                            onDelete={() => handleDelete(info.row.original.id)}
                        />
                    </div>
                ),
            },
        ],
        [handleDelete, newNoticeIds],
    );

    return (
        <Col className="flex w-full h-full relative bg-[#F4F5F7] justify-center items-center">
            <div className="flex w-[calc(100%-240px)] h-[1200px] flex-col bg-white items-center mt-[138px] rounded-[33px] p-[35px]">
                {/* Header */}
                <div className="flex justify-between items-center w-full mb-[45px]">
                    <Row className="flex gap-[15px] items-center">
                        <MediumText fontSize={25} color={'#3D4244'} letterSpacing={-0.7}>
                            공지 사항
                        </MediumText>
                        <RegularText fontSize={18} lineHeight={24} color={'#3D4244'} letterSpacing={-0.7}>
                            총 {totalResults}건의 공지가 있습니다.
                        </RegularText>
                    </Row>
                    <div
                        className="flex items-center bg-[#2D3132] rounded-[12px] px-[16px] py-[12px] cursor-pointer hover:bg-primary transition-colors"
                        onClick={handleOpenCreateModal}
                    >
                        <SvgIcon name="SvgPlus" size={14} fill={'#FFFFFF'} className="mr-[11px]" />
                        <RegularText fontSize={17} lineHeight={21} color={'#FFFFFF'} letterSpacing={-0.4}>
                            공지 사항 등록
                        </RegularText>
                    </div>
                </div>

                {/* Content */}
                {isLoading || (!isError && page > totalPages) ? (
                    <div className="flex flex-1 w-full items-center justify-center">
                        <RegularText fontSize={16} lineHeight={24} color="#A8AEB5">
                            불러오는 중...
                        </RegularText>
                    </div>
                ) : isError ? (
                    <div className="flex flex-1 w-full flex-col items-center justify-center gap-[16px]" role="alert">
                        <RegularText fontSize={16} lineHeight={24} color="#A8AEB5">
                            공지 사항을 불러오지 못했습니다.
                        </RegularText>
                        <button onClick={() => refetch()} disabled={isFetching}>
                            <RegularText fontSize={16} lineHeight={24} color="#A8AEB5">
                                다시 시도
                            </RegularText>
                        </button>
                    </div>
                ) : notices.length === 0 ? (
                    <div className="flex flex-1 w-full flex-col items-center justify-center gap-[16px]">
                        <div className="flex items-center justify-center w-[80px] h-[80px] rounded-full bg-[#F4F5F7]">
                            <SvgIcon name="SvgPencil" size={32} fill="#C6C8C9" />
                        </div>
                        <MediumText fontSize={18} lineHeight={27} color="#A8AEB5">
                            등록된 공지 사항이 없습니다.
                        </MediumText>
                        <RegularText fontSize={15} lineHeight={22} color="#C6C8C9">
                            상단의 공지 사항 등록 버튼을 눌러 새 공지를 작성해보세요.
                        </RegularText>
                    </div>
                ) : (
                    <div className="bg-white w-full">
                        <Table
                            data={notices}
                            columns={columns}
                            isIndex={true}
                            indexColumnWidth={50}
                            isPagination={true}
                            pageSize={NOTICE_PAGE_SIZE}
                            rowHeight={84}
                            headerColor="white"
                            hoverColor="#F0F2F5"
                            isUnballancedRowColor
                            indexFontSize={16}
                            indexHeader={
                                <DemiLightText fontSize={16} lineHeight={24} color={'#A8AEB5'}>
                                    No
                                </DemiLightText>
                            }
                            headerAndCellPadding={60}
                            onRowClick={(row: any) => handleOpenDetailModal(row)}
                            // 표는 수신한 한 페이지를 표시하고, 기존 버튼은 서버 페이지를 제어한다.
                            customPaginationComponent={() => {
                                return (
                                    <Row className="flex items-center self-center my-[22px]">
                                        <Row className="flex items-center gap-[30px] mr-[30px]">
                                            {Array.from({ length: Math.min(10, totalPages) }, (_, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => setPage(firstVisiblePage + i)}
                                                    aria-label={`공지 ${firstVisiblePage + i}페이지`}
                                                    aria-current={page === firstVisiblePage + i ? 'page' : undefined}
                                                    className={`w-[32px] h-[32px] rounded-full flex items-center justify-center ${
                                                        page === firstVisiblePage + i ? 'bg-[#3D4244]' : 'hover:bg-[#F0F0F0]'
                                                    }`}
                                                >
                                                    <MediumText
                                                        fontSize={16}
                                                        lineHeight={21}
                                                        color={page === firstVisiblePage + i ? '#FFFFFF' : colors.Text_1}
                                                    >
                                                        {firstVisiblePage + i}
                                                    </MediumText>
                                                </button>
                                            ))}
                                        </Row>
                                        <button
                                            onClick={() => setPage(Math.max(1, page - 1))}
                                            disabled={page <= 1}
                                            aria-label="이전 공지 페이지"
                                            className="disabled:opacity-30 disabled:cursor-not-allowed mr-[37px]"
                                        >
                                            <SvgIcon
                                                name={'SvgLeftStrokeArrow'}
                                                size={15}
                                                fill={colors.trans}
                                                stroke={'#70747C'}
                                                strokeWidth={1.2}
                                            />
                                        </button>

                                        <button
                                            onClick={() => setPage(Math.min(totalPages, page + 1))}
                                            disabled={page >= totalPages}
                                            aria-label="다음 공지 페이지"
                                            className="disabled:opacity-30 disabled:cursor-not-allowed"
                                        >
                                            <SvgIcon
                                                name={'SvgRightStrokeArrow'}
                                                size={15}
                                                fill={colors.trans}
                                                stroke={'#70747C'}
                                                strokeWidth={1.2}
                                            />
                                        </button>
                                    </Row>
                                );
                            }}
                        />
                    </div>
                )}
            </div>

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
}

export default NoticePage;
